import moment from 'moment'

import type { IAccountData, IPostData, CsvDownloadCommon } from './csv-download-common.service'

import { app } from '../../app.module'

interface ITikTokPostData extends IPostData {
  ttPostId: string
  plays: number
  likes: number
  comments: number
  shares: number
  collects: number
  engagements: number
  engagementRates: number
  accountUsername: string
}

interface ITtAccountData extends IAccountData {
  plays: number
  likes: number
  comments: number
  shares: number
  collects: number
  engagements: number
  engagementRate: number
}

/**
 * CsvダウンロードTikTokサービス
 */
export class CsvDownloadTikTok {
  constructor(
    private CsvDownloadCommon: CsvDownloadCommon,
    private $filter: any
  ) {}

  /**
   * 投稿CSVデータをセット
   * @param {ITikTokPostData[]} postDatas TikTok投稿データ
   * @returns {string[][]} CSVデータ
   */
  public setPostCsv(postDatas: ITikTokPostData[]): string[][] {
    // CSVデータ
    const csvData = []
    // ヘッダー
    const header = [
      'No',
      '投稿日付',
      'アカウント名',
      '大カテゴリ',
      '小カテゴリ',
      'フォロワー数',
      '投稿タイプ',
      '投稿内容',
      '再生回数',
      'いいね',
      'コメント',
      'シェア',
      'セーブ',
      '反応数',
      '反応率(%)',
      '投稿タグ',
      '画像URL',
      '投稿先URL',
      'アカウントID',
      '投稿ID',
    ]

    // ヘッダーを格納
    csvData.push(header)
    // データを格納
    postDatas.forEach((postData, index) => {
      // タグリスト
      let tagLists = ''

      // グローバルタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.globalTagNames)
      // グループタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.groupTagNames)
      // ユーザータグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.userTagNames)

      csvData.push([
        index + 1,
        moment(postData.publishDate).format('YYYY/MM/DD HH:mm'),
        postData.accountScreenName,
        postData.accountCategoryName,
        postData.accountSubCategoryName,
        postData.postDateFollowers,
        this.CsvDownloadCommon.convertPostType(postData.type),
        this.CsvDownloadCommon.convertPostMessage(postData.message),
        postData.plays,
        postData.likes,
        postData.comments,
        postData.shares,
        postData.collects,
        postData.engagements,
        this.CsvDownloadCommon.convertPercentage(postData.engagementRates),
        tagLists,
        postData.thumbnailUrl,
        `https://www.tiktok.com/@${postData.accountUsername}/${
          postData.type === 'carousel' ? 'photo' : 'video'
        }/${postData.ttPostId}`,
        postData.accountId,
        postData.ttPostId,
      ])
    })
    return csvData
  }

  /**
   * アカウント詳細のグラフCSVをセット
   * @param  {string} graphType グラフタイプ
   * @param  {boolean} dataPerObjectFlg 1投稿ごとフラグ(1アカウントごとフラグ)
   * @param {array} periodArray 期間リスト
   * @param  {object} data データ
   * @returns {string[][]} CSVデータ
   */
  public setAccountChangeCsv(
    graphType: string,
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    switch (graphType) {
      case 'respond':
        return this.setAccountChangeRespondCsv(dataPerObjectFlg, periodArray, data)
      case 'respond_rate':
        return this.CsvDownloadCommon.setAccountChangeRespondRateCsv(periodArray, data)
      case 'post_count':
        return this.CsvDownloadCommon.setAccountChangePostCount(dataPerObjectFlg, periodArray, data)
      case 'plays':
        return this.setAccountChangePlays(dataPerObjectFlg, periodArray, data)
    }
  }

  /**
   * アカウント詳細グラフの反応数CSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  private setAccountChangeRespondCsv(
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', 'いいね数', 'コメント数', 'シェア数', 'セーブ数', '反応数'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([
          periodArray[key],
          data.respondLikes[key],
          data.respondComments[key],
          data.respondShares[key],
          data.respondCollects[key],
          data.respondLikes[key] +
            data.respondComments[key] +
            data.respondShares[key] +
            data.respondCollects[key],
        ])
      }
    } else {
      csvData.push([
        '期間',
        'いいね数(1投稿あたり)',
        'コメント数(1投稿あたり)',
        'シェア数(1投稿あたり)',
        'セーブ数(1投稿あたり)',
        '反応数(1投稿あたり)',
      ])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        const engagementsPerPost =
          data.respondLikesPerPost[key] +
          data.respondCommentsPerPost[key] +
          data.respondSharesPerPost[key] +
          data.respondCollectsPerPost[key]

        csvData.push([
          periodArray[key],
          data.respondLikesPerPost[key],
          data.respondCommentsPerPost[key],
          data.respondSharesPerPost[key],
          data.respondCollectsPerPost[key],
          parseFloat(engagementsPerPost.toFixed(1)),
        ])
      }
    }

    return csvData
  }

  /**
   * アカウント詳細グラフの投稿数CSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  public setAccountChangePlays(dataPerObjectFlg: boolean, periodArray: {}[], data): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', '再生回数'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.plays[key]])
      }
    } else {
      csvData.push(['期間', '再生回数(1投稿あたり)'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([periodArray[key], data.playsPerPost[key]])
      }
    }

    return csvData
  }

  /**
   * アカウントの投稿の対象CSVデータをセット
   * @param  {string} targetName 対象名
   * @param  {boolean} dataPerPostFlg 1投稿ごとフラグ
   * @param  {array} data データ
   * @return {string[][]} CSVデータ
   */
  public setAccountTargetCsv(targetName: string, dataPerPostFlg: boolean, data: any[]): string[][] {
    const csvData = []

    // 1投稿ごとではない場合
    if (dataPerPostFlg === false) {
      csvData.push([
        targetName,
        '投稿数',
        '再生回数',
        'いいね',
        'コメント',
        'シェア',
        'セーブ',
        '反応数',
        '反応率',
      ])

      data.forEach((data) => {
        csvData.push([
          data.targetIndex,
          data.posts,
          data.plays,
          data.likes,
          data.comments,
          data.shares,
          data.collects,
          data.engagements,
          this.CsvDownloadCommon.convertPercentage(data.engagementRate),
        ])
      })
    } else {
      // 1投稿ごと場合
      csvData.push([
        targetName,
        '投稿数',
        '再生回数(1投稿あたり)',
        'いいね(1投稿あたり)',
        'コメント(1投稿あたり)',
        'シェア(1投稿あたり)',
        'セーブ(1投稿あたり)',
        '反応数(1投稿あたり)',
        '反応率',
      ])

      data.forEach((data) => {
        csvData.push([
          data.targetIndex,
          data.posts,
          data.playsPerPost,
          data.likesPerPost,
          data.commentsPerPost,
          data.sharesPerPost,
          data.collectsPerPost,
          data.engagementsPerPost,
          this.CsvDownloadCommon.convertPercentage(data.engagementRate),
        ])
      })
    }

    return csvData
  }

  /**
   * アカウントCSVデータをセット
   */
  public setAccountCsv(accountDatas: ITtAccountData[], dataPerPost: boolean): string[][] {
    // CSVデータ
    const csvData = []
    // ヘッダー
    let header = []

    if (dataPerPost === true) {
      header = [
        'No',
        'アカウント名',
        'ブランド名',
        '大カテゴリ',
        '小カテゴリ',
        'フォロワー数',
        'フォロワー増減率(%)',
        '再生回数(1投稿あたり)',
        'いいね(1投稿あたり)',
        'コメント(1投稿あたり)',
        'シェア(1投稿あたり)',
        'セーブ(1投稿あたり)',
        '反応数(1投稿あたり)',
        '反応率(%)',
        '投稿数',
        'アカウントタグ',
      ]
    } else {
      header = [
        'No',
        'アカウント名',
        'ブランド名',
        '大カテゴリ',
        '小カテゴリ',
        'フォロワー数',
        'フォロワー増減率(%)',
        '再生回数',
        'いいね',
        'コメント',
        'シェア',
        'セーブ',
        '反応数',
        '反応率(%)',
        '投稿数',
        'アカウントタグ',
      ]
    }

    // ヘッダーを格納
    csvData.push(header)

    // データを格納
    accountDatas.forEach((account, index) => {
      const engageData = [
        account.plays,
        account.likes,
        account.comments,
        account.shares,
        account.collects,
        account.engagements,
      ]
      const ratioData = [
        this.CsvDownloadCommon.convertPercentage(account.engagementRate),
        account.posts,
      ]

      // 1投稿ごとの反応数が選択されている場合は、反応数を投稿数ごとに割る
      if (dataPerPost === true) {
        engageData.forEach((element, index) => {
          engageData[index] = this.$filter('setNumberFormat')(
            element,
            1,
            'number',
            true,
            account.posts
          )
        })
      }
      // タグリスト
      let tagLists = ''

      // グローバルタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.globalTagNames)
      // グループタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.groupTagNames)
      // ユーザータグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.userTagNames)

      // フォロワー増減率
      let followersUpDownRate = '-'
      if (account.followersUpdownRate !== null) {
        followersUpDownRate = String(account.followersUpdownRate)
      }

      let accountData = [
        index + 1,
        account.screenName,
        account.brandName,
        account.categoryName,
        account.subCategoryName,
        account.followers,
        followersUpDownRate,
      ]

      accountData = accountData.concat(engageData, ratioData, [tagLists])

      csvData.push(accountData)
    })

    return csvData
  }
}

// CSVダウンロード用のサービスを定義
app.service('CsvDownloadTikTok', CsvDownloadTikTok)
